import { useState, useEffect, useRef, useMemo } from "react";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import useCurrentUser from "hooks/useCurrentUser";
import {
  PageContainer, PremiumCard, Button, Field, SelectField,
  LoadingState,
} from "components/ui";

const IconUpload = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);
const IconFile = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);
const IconCheck = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const IconPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const IconX = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const IconAlert = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const STEPS = [
  { key: "upload",   label: "Upload" },
  { key: "scanning", label: "AI scan" },
  { key: "review",   label: "Review" },
  { key: "saving",   label: "Save" },
  { key: "done",     label: "Done" },
];

// The standard Tunisian VAT rates in use across the catalog. Kept as a flat
// list here (rather than pulled from /api/categories) because VAT rate is a
// property of the invoice line, not of the category — a line can carry a
// different rate than what its matched/new category would default to, and
// the user needs to be able to override it directly against what's printed
// on the paper invoice.
const VAT_RATES = [0, 7, 13, 19];

// Reconciliation tolerance: printed invoice totals and our own re-summed
// totals can differ by a fraction of a millime from rounding order-of-
// operations (per-line rounding vs rounding the sum). Anything beyond this
// is a real discrepancy worth flagging, not float noise.
const RECONCILE_EPSILON = 0.01;

// Money fields are stored backend-side as BigDecimal(precision=19, scale=3).
// Round every monetary value to 3 decimals on the frontend too, so what the
// user sees/edits matches exactly what gets persisted — plain float math
// (e.g. 0.1 * 3) can otherwise produce values like 0.30000000000000004
// that silently drift once they hit the backend's own rounding.
const round3 = (n) => Math.round((parseFloat(n) || 0) * 1000) / 1000;

function extractErrorMessage(err, fallback) {
  const data = err.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (data && typeof data === "object" && data.message) return data.message;
  return err.message || fallback;
}

function StepIndicator({ step }) {
  const current = STEPS.findIndex((s) => s.key === step);
  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: 0,
        padding: "20px 24px",
        background: "var(--surface)",
        border: "1px solid var(--border-subtle)",
        borderRadius: 14,
        marginBottom: 16,
        boxShadow: "var(--shadow-sm)",
      }}
    >
      {STEPS.map((s, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <div key={s.key} style={{ display: "flex", alignItems: "center", flex: i < STEPS.length - 1 ? 1 : 0, minWidth: 0 }}>
            <div
              style={{
                width: 28, height: 28, borderRadius: 999,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                fontSize: 12, fontWeight: 700, flexShrink: 0,
                background: done ? "var(--success)" : active ? "var(--brand-primary)" : "var(--surface-sunken)",
                color: done || active ? "#fff" : "var(--text-muted)",
                boxShadow: active ? "var(--shadow-ring)" : "none",
                transition: "all var(--duration-base) var(--ease)",
              }}
            >
              {done ? <IconCheck /> : i + 1}
            </div>
            <span
              style={{
                marginLeft: 10,
                fontSize: 13,
                fontWeight: active ? 600 : 500,
                color: active ? "var(--text-primary)" : done ? "var(--text-body)" : "var(--text-muted)",
                whiteSpace: "nowrap",
              }}
            >
              {s.label}
            </span>
            {i < STEPS.length - 1 && (
              <div
                style={{
                  flex: 1, height: 2, margin: "0 14px",
                  background: done ? "var(--success)" : "var(--surface-sunken)",
                  borderRadius: 999,
                  transition: "background var(--duration-base) var(--ease)",
                  minWidth: 18,
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function InvoiceScanner() {
  const axiosPrivate = useAxiosPrivate();
  const { user } = useCurrentUser();
  const branch = user?.branch;

  const [step, setStep] = useState("upload");
  const [items, setItems] = useState([]);
  const [supplierName, setSupplier] = useState("");
  const [invoiceNumber, setInvoiceNo] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [categories, setCategories] = useState([]);
  const [dragging, setDragging] = useState(false);
  // Printed totals from the invoice itself, when the scan endpoint returns
  // them (DGT-format Tunisian invoices always print Total HT / VAT / stamp
  // duty / TTC). Null when the backend hasn't provided them — in that case
  // the reconciliation banner simply doesn't render, rather than false-
  // alarming against nothing.
  const [invoiceTotals, setInvoiceTotals] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    if (!branch) return;
    axiosPrivate.get("/api/categories")
      .then((res) => setCategories(res.data))
      .catch(() => setError("Could not load categories."));
  }, [branch]);

  const autoClassify = async (itemsList) => {
    const updated = [...itemsList];
    await Promise.all(
      updated.map(async (item, idx) => {
        if (!item.matched && !item.categoryId) {
          try {
            const res = await axiosPrivate.get(
              `/api/categories/classify?name=${encodeURIComponent(item.productName)}`,
            );
            if (res.data && res.data.id) updated[idx] = { ...updated[idx], categoryId: res.data.id };
          } catch (_) {}
        }
      }),
    );
    setItems(updated);
  };

  async function processFile(file) {
    if (!file) return;
    setError("");
    setStep("scanning");
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await axiosPrivate.post("/api/invoices/scan", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // The scan endpoint may return either a bare array of line items
      // (legacy shape) or an object { items, invoiceTotals } once the
      // backend also extracts the invoice's own printed totals. Support
      // both so this component keeps working before/after that backend
      // change ships.
      const rawItems = Array.isArray(res.data) ? res.data : res.data.items || [];
      const rawTotals = Array.isArray(res.data) ? null : res.data.invoiceTotals || null;

      // Normalize whatever the AI scan returns to 3-decimal money values
      // right away, so state never carries un-rounded floats.
      const newItems = rawItems.map((it, i) => ({
        ...it,
        _key: i,
        categoryId: null,
        vatRate: VAT_RATES.includes(Number(it.vatRate)) ? Number(it.vatRate) : (it.vatRate ?? 19),
        unitPriceHT: round3(it.unitPriceHT),
        totalHT: round3(it.totalHT),
      }));
      setItems(newItems);
      setInvoiceTotals(
        rawTotals
          ? {
              totalHT: round3(rawTotals.totalHT),
              totalVAT: round3(rawTotals.totalVAT),
              stampDuty: round3(rawTotals.stampDuty),
              totalTTC: round3(rawTotals.totalTTC),
            }
          : null,
      );
      setStep("review");
      autoClassify(newItems);
    } catch (err) {
      setError("Scan failed: " + extractErrorMessage(err, "Unknown error"));
      setStep("upload");
    }
  }
  function handleFile(e) {
    processFile(e.target.files[0]);
    e.target.value = "";
  }
  function handleDrop(e) {
    e.preventDefault(); setDragging(false);
    processFile(e.dataTransfer.files[0]);
  }

  // Backend expects/returns unitPriceHT + totalHT (+ vatRate, totalTTC as
  // informational fields), all BigDecimal(precision=19, scale=3) — every
  // write to these fields goes through round3 so state stays scale-safe.
  function update(key, field, value) {
    setItems((prev) =>
      prev.map((it) => {
        if (it._key !== key) return it;
        const next = { ...it, [field]: value };
        if (field === "unitPriceHT") {
          next.unitPriceHT = round3(value);
        }
        if (field === "vatRate") {
          next.vatRate = Number(value);
        }
        if (field === "quantity" || field === "unitPriceHT") {
          next.totalHT = round3(
            (parseFloat(next.quantity) || 0) * (parseFloat(next.unitPriceHT) || 0)
          );
        }
        if (field === "quantity" || field === "unitPriceHT" || field === "vatRate") {
          next.totalTTC = round3(
            (parseFloat(next.totalHT) || 0) * (1 + (parseFloat(next.vatRate) || 0) / 100)
          );
        }
        return next;
      }),
    );
  }
  const removeRow = (key) => setItems((prev) => prev.filter((it) => it._key !== key));
  const addRow = () =>
    setItems((prev) => [
      ...prev,
      {
        _key: Date.now(),
        productName: "",
        sku: "",
        barcode: "",
        quantity: 1,
        unitPriceHT: 0,
        totalHT: 0,
        vatRate: 19,
        totalTTC: 0,
        matched: false,
        productId: null,
        categoryId: null,
      },
    ]);

  const grandTotal = round3(items.reduce((s, it) => s + (parseFloat(it.totalHT) || 0), 0));
  const missingCategoryItem = items.find((it) => !it.matched && !it.categoryId);

  // Re-sum VAT and TTC from the line items exactly the way the invoice
  // itself does: per-line VAT summed, not (sum of HT) * some blended rate —
  // that distinction is what let the misclassified-rate bug through review
  // undetected in the first place.
  const computedTotals = useMemo(() => {
    const totalHT = grandTotal;
    const totalVAT = round3(
      items.reduce((s, it) => s + (parseFloat(it.totalHT) || 0) * ((parseFloat(it.vatRate) || 0) / 100), 0)
    );
    const stampDuty = invoiceTotals ? invoiceTotals.stampDuty : 0;
    const totalTTC = round3(totalHT + totalVAT + stampDuty);
    return { totalHT, totalVAT, stampDuty, totalTTC };
  }, [items, grandTotal, invoiceTotals]);

  // Compare what we scanned/edited against what the invoice actually prints.
  // A mismatch here is exactly the failure mode that let a wrong VAT rate
  // slip through silently — surface it before the user can confirm.
  const reconciliation = useMemo(() => {
    if (!invoiceTotals) return null;
    const diffs = {
      totalHT: round3(computedTotals.totalHT - invoiceTotals.totalHT),
      totalVAT: round3(computedTotals.totalVAT - invoiceTotals.totalVAT),
      totalTTC: round3(computedTotals.totalTTC - invoiceTotals.totalTTC),
    };
    const mismatched = Object.values(diffs).some((d) => Math.abs(d) > RECONCILE_EPSILON);
    return { diffs, mismatched };
  }, [computedTotals, invoiceTotals]);

  async function confirm() {
    if (!branch)   { setError("No branch assigned."); return; }
    if (!items.length) { setError("No items to save."); return; }
    if (missingCategoryItem) {
      setError(`"${missingCategoryItem.productName || "This item"}" needs a category selected before saving — it's required to calculate VAT.`);
      return;
    }
    setError("");
    setStep("saving");
    try {
      // Final safety net: round money fields right before they leave the
      // client, regardless of what state happens to hold at this point.
      const payload = items.map(({ _key, unitPriceHT, totalHT, totalTTC, vatRate, ...rest }) => ({
        ...rest,
        unitPriceHT: round3(unitPriceHT),
        totalHT: round3(totalHT),
        vatRate: parseFloat(vatRate) || 0,
        totalTTC: round3(totalTTC),
      }));
      const res = await axiosPrivate.post("/api/invoices/confirm", {
        branchId: branch.id,
        supplierName, invoiceNumber,
        items: payload,
      });
      setResult(res.data);
      setStep("done");
    } catch (err) {
      setError("Save failed: " + extractErrorMessage(err, "Unknown error"));
      setStep("review");
    }
  }

  function reset() {
    setStep("upload"); setItems([]); setSupplier(""); setInvoiceNo("");
    setResult(null); setError(""); setInvoiceTotals(null);
  }

  const inputCell = {
    border: "1px solid var(--border-subtle)",
    background: "var(--surface)",
    borderRadius: 8,
    padding: "6px 8px",
    fontSize: 13,
    fontFamily: "var(--font-body)",
    width: "100%",
    // Without this, padding is added ON TOP of the 100% width (content-box
    // default), so the input overflows its <td> and the browser clips the
    // visible text — that's what was cutting off qty and TVA values in
    // their narrow columns.
    boxSizing: "border-box",
    outline: 0,
    transition: "border-color var(--duration-fast) var(--ease), box-shadow var(--duration-fast) var(--ease)",
  };

  // Number inputs (qty, unit price) render native spin-button arrows that
  // eat 15-20px of the already-tight column width. Hide them — the columns
  // are too narrow to afford that space, and users can still type directly.
  const numberCell = {
    ...inputCell,
    textAlign: "right",
    fontFamily: "var(--font-mono)",
  };

  return (
    <PageContainer>
      <StepIndicator step={step} />

      {error && (
        <div
          role="alert"
          style={{
            background: "var(--danger-bg)", color: "#991B1B",
            border: "1px solid var(--danger-border)", borderRadius: 12,
            padding: "12px 16px", fontSize: 14, marginBottom: 16,
            display: "flex", alignItems: "center", gap: 10,
          }}
        >
          <span aria-hidden="true">⚠️</span>{error}
        </div>
      )}

      {/* UPLOAD */}
      {step === "upload" && (
        <PremiumCard title="Scan a supplier invoice" subtitle="Upload an image or PDF — AI extracts products, quantities, and prices.">
          <div
            onClick={() => fileRef.current.click()}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            role="button"
            tabIndex={0}
            style={{
              border: `2px dashed ${dragging ? "var(--brand-primary)" : "var(--border-default)"}`,
              borderRadius: 16,
              padding: "56px 24px",
              textAlign: "center",
              background: dragging ? "var(--brand-primary-50)" : "var(--surface-muted)",
              cursor: "pointer",
              transition: "all var(--duration-base) var(--ease)",
            }}
          >
            <div
              style={{
                width: 64, height: 64, borderRadius: 16,
                background: "linear-gradient(135deg, var(--brand-primary), var(--brand-accent))",
                color: "#fff",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 16px",
                boxShadow: "0 12px 28px rgba(79,70,229,0.32)",
              }}
            >
              <IconUpload />
            </div>
            <h3 className="t-heading-md" style={{ margin: 0 }}>Drop your invoice here</h3>
            <p className="t-body-sm t-muted" style={{ margin: "8px 0 0" }}>
              JPG · PNG · PDF — photo or scanned document
            </p>
            <p className="t-body-xs" style={{ margin: "16px 0 0" }}>
              Powered by Mistral AI · Free tier
            </p>
            <input ref={fileRef} type="file" accept="image/*,.pdf" onChange={handleFile} style={{ display: "none" }} />
          </div>
        </PremiumCard>
      )}

      {/* SCANNING / SAVING */}
      {(step === "scanning" || step === "saving") && (
        <PremiumCard>
          <LoadingState
            label={step === "scanning"
              ? "AI is reading the invoice…"
              : "Saving to database…"}
          />
          <p className="t-body-sm t-muted" style={{ textAlign: "center", marginTop: 0 }}>
            {step === "scanning"
              ? "Detecting products, quantities and prices"
              : "Creating purchase and updating stock levels"}
          </p>
        </PremiumCard>
      )}

      {/* REVIEW */}
      {step === "review" && (
        <>
          <PremiumCard
            className="mb-3"
            title="Invoice details"
            subtitle={`AI extracted ${items.length} line item${items.length !== 1 ? "s" : ""} — review before saving`}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 16,
              }}
            >
              <Field label="Branch" disabled value={branch?.name || "No branch"} readOnly />
              <Field label="Supplier name" value={supplierName} onChange={(e) => setSupplier(e.target.value)} placeholder="e.g. SONEPAR Tunisie" />
              <Field label="Invoice number" value={invoiceNumber} onChange={(e) => setInvoiceNo(e.target.value)} placeholder="e.g. INV-2024-0042" />
            </div>
          </PremiumCard>

          {reconciliation?.mismatched && (
            <div
              role="alert"
              style={{
                background: "var(--warning-bg)", color: "#92400E",
                border: "1px solid var(--warning-border, #FDE68A)", borderRadius: 12,
                padding: "14px 16px", fontSize: 13.5, marginBottom: 16,
                display: "flex", alignItems: "flex-start", gap: 10,
              }}
            >
              <span style={{ flexShrink: 0, marginTop: 1 }}><IconAlert /></span>
              <div>
                <strong>Scanned totals don't match the invoice's printed totals.</strong>
                <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: "4px 18px" }}>
                  {Math.abs(reconciliation.diffs.totalVAT) > RECONCILE_EPSILON && (
                    <span>VAT differs by {reconciliation.diffs.totalVAT > 0 ? "+" : ""}{reconciliation.diffs.totalVAT.toFixed(3)} TND</span>
                  )}
                  {Math.abs(reconciliation.diffs.totalHT) > RECONCILE_EPSILON && (
                    <span>Goods HT differs by {reconciliation.diffs.totalHT > 0 ? "+" : ""}{reconciliation.diffs.totalHT.toFixed(3)} TND</span>
                  )}
                  {Math.abs(reconciliation.diffs.totalTTC) > RECONCILE_EPSILON && (
                    <span>Total TTC differs by {reconciliation.diffs.totalTTC > 0 ? "+" : ""}{reconciliation.diffs.totalTTC.toFixed(3)} TND</span>
                  )}
                </div>
                <div style={{ marginTop: 4, opacity: 0.85 }}>
                  Check the TVA rate on each line against the paper invoice before confirming.
                </div>
              </div>
            </div>
          )}

          <PremiumCard
            title="Line items"
            actions={
              <>
                <Button variant="secondary" size="sm" leftIcon={<IconPlus />} onClick={addRow}>Add row</Button>
                <Button variant="primary"   size="sm" onClick={confirm} disabled={!items.length}>
                  Confirm & save
                </Button>
              </>
            }
            flush
          >
            <div style={{ overflowX: "auto" }}>
              <table className="dt-table" style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th style={{ width: 76 }}></th>
                    <th style={{ minWidth: 180 }}>Product name</th>
                    <th style={{ width: 120 }}>SKU</th>
                    <th style={{ width: 140 }}>Barcode</th>
                    <th style={{ width: 80, textAlign: "right" }}>Qty</th>
                    <th style={{ width: 110, textAlign: "right" }}>Unit price (HT)</th>
                    <th style={{ width: 120, textAlign: "right" }}>Total (HT)</th>
                    <th style={{ width: 100, textAlign: "right" }}>TVA</th>
                    <th style={{ width: 160 }}>Category (new)</th>
                    <th style={{ width: 40 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const needsCategory = !item.matched && !item.categoryId;
                    return (
                      <tr key={item._key} style={needsCategory ? { background: "var(--warning-bg)" } : undefined}>
                        <td>
                          <span className={item.matched ? "sb sb--success" : "sb sb--warning"} style={{ padding: "3px 8px" }}>
                            {item.matched ? "known" : "new"}
                          </span>
                        </td>
                        <td>
                          <input style={inputCell} value={item.productName}
                                 onChange={(e) => update(item._key, "productName", e.target.value)} />
                        </td>
                        <td>
                          <input style={inputCell} value={item.sku || ""}
                                 onChange={(e) => update(item._key, "sku", e.target.value)}
                                 placeholder="—" />
                        </td>
                        <td>
                          <input style={inputCell} value={item.barcode || ""}
                                 onChange={(e) => update(item._key, "barcode", e.target.value)}
                                 placeholder="—" />
                        </td>
                        <td>
                          <input style={{ ...inputCell, textAlign: "right", fontFamily: "var(--font-mono)" }}
                                 type="number" min="0" step="1" value={item.quantity}
                                 onChange={(e) => update(item._key, "quantity", e.target.value)} />
                        </td>
                        <td>
                          <input style={{ ...inputCell, textAlign: "right", fontFamily: "var(--font-mono)" }}
                                 type="number" min="0" step="0.001" value={item.unitPriceHT}
                                 onChange={(e) => update(item._key, "unitPriceHT", e.target.value)} />
                        </td>
                        <td style={{ textAlign: "right", fontWeight: 600, fontFamily: "var(--font-mono)" }}>
                          {(parseFloat(item.totalHT) || 0).toFixed(3)}
                          <span style={{ marginLeft: 4, color: "var(--text-muted)", fontWeight: 400, fontSize: 11 }}>TND</span>
                        </td>
                        <td>
                          <select
                            style={{ ...inputCell, textAlign: "right", fontFamily: "var(--font-mono)" }}
                            value={VAT_RATES.includes(Number(item.vatRate)) ? item.vatRate : ""}
                            onChange={(e) => update(item._key, "vatRate", e.target.value)}
                          >
                            {!VAT_RATES.includes(Number(item.vatRate)) && (
                              <option value="">{item.vatRate}%?</option>
                            )}
                            {VAT_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
                          </select>
                        </td>
                        <td>
                          {!item.matched && (
                            <select style={inputCell} value={item.categoryId || ""}
                                    onChange={(e) => update(item._key, "categoryId", e.target.value || null)}>
                              <option value="">— None —</option>
                              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                          )}
                        </td>
                        <td>
                          <button
                            onClick={() => removeRow(item._key)}
                            title="Remove row"
                            style={{
                              width: 30, height: 30, borderRadius: 8,
                              border: 0, background: "transparent",
                              color: "var(--text-muted)", cursor: "pointer",
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = "var(--danger-bg)"; e.currentTarget.style.color = "var(--danger)"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--text-muted)"; }}
                          >
                            <IconX />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div
              style={{
                padding: "16px 24px",
                background: "var(--surface-muted)",
                borderTop: "1px solid var(--border-subtle)",
                display: "flex", flexDirection: "column", gap: 8,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                <span className="t-muted">Goods subtotal (excl. VAT &amp; stamp duty)</span>
                <strong className="t-mono" style={{ fontSize: 16, color: "var(--text-primary)" }}>
                  {computedTotals.totalHT.toFixed(3)} TND
                </strong>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                <span className="t-muted">VAT (sum of line items)</span>
                <span className="t-mono">{computedTotals.totalVAT.toFixed(3)} TND</span>
              </div>
              {invoiceTotals && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                  <span className="t-muted">Stamp duty (from invoice)</span>
                  <span className="t-mono">{computedTotals.stampDuty.toFixed(3)} TND</span>
                </div>
              )}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                <span className="t-muted" style={{ fontWeight: 600 }}>Total TTC</span>
                <strong className="t-mono" style={{ fontSize: 16, color: "var(--text-primary)" }}>
                  {computedTotals.totalTTC.toFixed(3)} TND
                </strong>
              </div>
              {invoiceTotals && (
                <div className="t-body-xs t-muted" style={{ marginTop: 2 }}>
                  Printed on invoice: {invoiceTotals.totalHT.toFixed(3)} HT · {invoiceTotals.totalVAT.toFixed(3)} VAT · {invoiceTotals.totalTTC.toFixed(3)} TTC
                </div>
              )}
              {missingCategoryItem && (
                <div className="t-body-xs" style={{ color: "var(--warning)" }}>
                  ⚠️ One or more new items need a category before saving
                </div>
              )}
            </div>
          </PremiumCard>
        </>
      )}

      {/* DONE */}
      {step === "done" && result && (
        <PremiumCard>
          <div style={{ textAlign: "center", padding: "24px 8px" }}>
            <div
              style={{
                width: 72, height: 72, borderRadius: 18,
                background: "linear-gradient(135deg, var(--success-bg), #D1FAE5)",
                color: "var(--success)",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 20px",
                boxShadow: "0 8px 24px rgba(16,185,129,0.2)",
              }}
            >
              <IconCheck />
            </div>
            <h3 className="t-heading-lg" style={{ margin: 0 }}>Purchase created</h3>
            <p className="t-body-sm t-muted" style={{ margin: "10px 0 4px" }}>
              {result.items?.length ?? items.length} products added to stock
            </p>
            <p className="t-body-sm t-muted" style={{ margin: "0 0 24px" }}>
              Total: <strong className="t-mono">{Number(result.totalAmount ?? grandTotal).toFixed(3)} TND</strong>
            </p>
            <Button variant="primary" leftIcon={<IconFile />} onClick={reset}>
              Scan another invoice
            </Button>
          </div>
        </PremiumCard>
      )}
    </PageContainer>
  );
}